import React, { useEffect, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { PageHeader, Card, Empty, Spinner } from "@/components/UIKit";
import { fmtNOK, fmtDate, fmtDateTime } from "@/lib/format";
import { AlertTriangle, Download, FileText, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Inkasso() {
  const [days, setDays] = useState(45);
  const [data, setData] = useState(null);
  const [exports, setExports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [agency, setAgency] = useState("kredinor");
  const [format, setFormat] = useState("csv");

  const load = async () => {
    const [c, e] = await Promise.all([
      api.get(`/collection/candidates?days_overdue=${days}`),
      api.get("/collection/exports"),
    ]);
    setData(c.data); setExports(e.data);
  };

  useEffect(() => { load(); }, [days]);

  const exportNow = async () => {
    setBusy(true);
    try {
      const resp = await fetch(`${API}/collection/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ agency, format, days_overdue: days, mark_as_inkasso: true }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || "Feilet");
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `inkasso-${agency}-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${data.count} fakturaer eksportert til ${agency} og markert som inkasso`);
      load();
    } catch (e) {
      toast.error(e.message || "Eksport feilet");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="inkasso-page">
      <PageHeader
        title="Inkasso"
        subtitle="Overføring av forfalte fakturaer til inkassoselskap"
        actions={
          <div className="flex items-center gap-1 bg-zinc-900/60 rounded-md p-1 border border-zinc-900">
            {[45, 60, 90, 120].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1 rounded text-xs transition-colors ${days === d ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-200"}`}
                data-testid={`overdue-${d}`}>
                +{d}d
              </button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <Card className="p-5" testid="kpi-collection-count">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-red-400" /> Kandidater
          </div>
          <div className="font-heading text-3xl tabular-nums text-red-400">{data?.count || 0}</div>
          <div className="text-xs text-zinc-500 mt-1">Fakturaer eldre enn {days} dager</div>
        </Card>
        <Card className="p-5" testid="kpi-collection-sum">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Utestående sum</div>
          <div className="font-heading text-3xl tabular-nums text-red-400">{fmtNOK(data?.total_outstanding_nok || 0)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Tidligere eksporter</div>
          <div className="font-heading text-3xl tabular-nums">{exports.length}</div>
        </Card>
      </div>

      <Card className="p-5 mb-4">
        <div className="font-heading font-medium mb-3">Eksporter til inkassoselskap</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] uppercase text-zinc-500 mb-1">Inkassoselskap</label>
            <select value={agency} onChange={(e) => setAgency(e.target.value)}
              className="h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" data-testid="agency-select">
              <option value="kredinor">Kredinor</option>
              <option value="lindorff">Lindorff (Lowell)</option>
              <option value="intrum">Intrum</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase text-zinc-500 mb-1">Format</label>
            <select value={format} onChange={(e) => setFormat(e.target.value)}
              className="h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" data-testid="format-select">
              <option value="csv">CSV</option>
              <option value="xml">XML (UBL)</option>
            </select>
          </div>
          <button onClick={exportNow} disabled={busy || !data?.count}
            className="h-10 px-5 rounded-md bg-red-500 hover:bg-red-400 disabled:opacity-40 text-white text-sm font-medium flex items-center gap-2"
            data-testid="export-inkasso">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Eksporter {data?.count || 0} fakturaer
          </button>
        </div>
        <div className="text-xs text-zinc-500 mt-3">
          Fakturaene markeres som <span className="text-red-400 font-medium">inkasso</span> etter eksport. Filen lastes ned til deg og skal videresendes til {agency}.
        </div>
      </Card>

      <Card className="overflow-hidden mb-4">
        <div className="p-4 border-b border-zinc-900 font-heading font-medium">Kandidater ({data?.count || 0})</div>
        {!data ? <div className="p-6"><Spinner /></div>
          : data.items.length === 0 ? <Empty title="Ingen fakturaer eldre enn valgt periode" subtitle="Alt er innkrevd — god jobb!" />
          : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60 sticky top-0">
                <th className="py-2 px-4 font-medium">Fakturanr</th>
                <th className="py-2 px-4 font-medium">Kunde</th>
                <th className="py-2 px-4 font-medium">Forfall</th>
                <th className="py-2 px-4 font-medium">Dager forfalt</th>
                <th className="py-2 px-4 font-medium text-right">Utestående</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`inkasso-row-${i.invoice_number}`}>
                  <td className="py-2 px-4"><Link to={`/fakturering/${i.id}`} className="font-mono text-xs text-emerald-400">{i.invoice_number}</Link></td>
                  <td className="py-2 px-4">
                    <div className="text-zinc-100">{i.customer?.full_name}</div>
                    <div className="text-[10px] text-zinc-500">{i.customer?.customer_number}{i.customer?.orgnr ? ` · orgnr ${i.customer.orgnr}` : ""}</div>
                  </td>
                  <td className="py-2 px-4 text-zinc-400 text-xs">{fmtDate(i.due_date)}</td>
                  <td className="py-2 px-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${i.days_overdue > 90 ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-orange-500/10 text-orange-400 border border-orange-500/20"}`}>
                      {i.days_overdue} dager
                    </span>
                  </td>
                  <td className="py-2 px-4 tabular-nums text-right text-red-400">{fmtNOK(i.outstanding_nok)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {exports.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-zinc-900 font-heading font-medium">Tidligere eksporter</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-zinc-500 bg-zinc-950/60">
                <th className="py-2 px-4 font-medium">Dato</th>
                <th className="py-2 px-4 font-medium">Selskap</th>
                <th className="py-2 px-4 font-medium">Format</th>
                <th className="py-2 px-4 font-medium">Fakturaer</th>
                <th className="py-2 px-4 font-medium text-right">Beløp</th>
              </tr>
            </thead>
            <tbody>
              {exports.map((e) => (
                <tr key={e.id} className="border-t border-zinc-900">
                  <td className="py-2 px-4 text-zinc-400 text-xs">{fmtDateTime(e.exported_at)}</td>
                  <td className="py-2 px-4 text-zinc-100 capitalize">{e.agency}</td>
                  <td className="py-2 px-4 text-zinc-400 uppercase text-xs">{e.format}</td>
                  <td className="py-2 px-4 tabular-nums">{e.invoice_count}</td>
                  <td className="py-2 px-4 tabular-nums text-right text-zinc-300">{fmtNOK(e.total_amount_nok)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
